import {
  type Color,
  FLAG_ACTION,
  KING,
  TYPE_MASK,
  WHITE,
  BLACK,
  colorBit,
  encodeMove,
  isAction,
  isDrop,
  moveFrom,
  moveTo,
  other,
  parseSquare,
  pieceColor,
  squareName,
} from '../board.ts';
import { pieceDef } from '../pieces.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, draw, win } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 20 (draft). The king has heirs. There's no check: kings can be captured. When yours is,
// you crown any of your other pieces except a pawn (a free action at the start of your turn,
// then you move as usual). The new king keeps its own moves, so a crowned knight still jumps.
// You lose when your king is captured and you have nobody left to crown.
//
// ext: [white royal square + 1, black royal square + 1] (0: none), mirroring `royals` so the
// hash knows which piece wears the crown.

export const HEIR_FEN = `${STANDARD_FEN} e1 e8`;

/** Pieces of `c` that could be crowned: anything but pawns (and the current royal). */
export function heirsOf(pos: Position, c: Color): number[] {
  const bit = colorBit(c);
  const out: number[] = [];
  for (let sq = 0; sq < 64; sq++) {
    const p = pos.board[sq];
    if (!(p & bit) || sq === pos.royals[c]) continue;
    if (!pieceDef(p & TYPE_MASK).pawn) out.push(sq);
  }
  return out;
}

/** The side to move lost its king and must crown an heir before moving. */
export function crowning(pos: Position): boolean {
  return pos.royals[pos.turn] < 0;
}

function syncExt(pos: Position) {
  pos.ext![0] = pos.royals[WHITE] + 1;
  pos.ext![1] = pos.royals[BLACK] + 1;
}

export const heir: Variant = {
  id: 'heir',
  info: {
    name: 'Heir Chess',
    but: 'the king has heirs',
    tagline: 'Take the king and someone else just puts on the crown. Run out of heirs and you lose.',
    summary: 'There’s no check: **capture the king**. Then its owner crowns another piece, which keeps its moves. Lose your last heir and you lose.',
    rules: [
      'There’s no check. You may leave your king attacked, and kings can be captured.',
      'When your king is captured, you crown any of your other pieces except a pawn. Then you take your turn as usual.',
      'The new king keeps its own moves: a crowned knight still jumps, a crowned queen still slides.',
      'You lose when your king is captured and you have no piece left to crown.',
      'Only the original king can castle. Stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#a98bff',
    emoji: '👑',
    bots: ['Squire', 'Baron', 'Duke', 'Prince', 'Emperor'],
  },
  startFen: HEIR_FEN,
  checks: false,
  loseWithoutRoyal: false,
  extSize: 2,

  parseExtra(fields, pos) {
    const [w = '-', b = '-'] = fields;
    for (const [c, field] of [[WHITE, w], [BLACK, b]] as [Color, string][]) {
      if (field === '-') {
        if (pos.royals[c] >= 0) throw new FenError('A king is always royal');
        continue;
      }
      const sq = parseSquare(field);
      const p = sq < 0 ? 0 : pos.board[sq];
      if (!p || pieceColor(p) !== c || pieceDef(p & TYPE_MASK).pawn) throw new FenError('Bad royal square');
      // A crowned piece replaces the king, so there can't be another king of that colour.
      if (pos.royals[c] >= 0 && pos.royals[c] !== sq) throw new FenError('Two royals for one side');
      pos.royals[c] = sq;
    }
    if (pos.royals[WHITE] < 0 && pos.royals[BLACK] < 0) throw new FenError('Somebody needs a king');
    syncExt(pos);
  },

  formatExtra(pos) {
    return [pos.royals[WHITE], pos.royals[BLACK]].map((sq) => (sq >= 0 ? squareName(sq) : '-'));
  },

  replaceMoves(pos, out) {
    if (!crowning(pos)) return false;
    for (const sq of heirsOf(pos, pos.turn)) out.push(encodeMove(sq, sq, 0, FLAG_ACTION));
    return true;
  },

  afterMove(ctx: MoveContext) {
    const { next, prev, us, move } = ctx;
    if (isAction(move)) {
      next.royals[us] = moveFrom(move);
      // Crowning isn't a move: it doesn't count towards the 50-move rule.
      next.halfmove = prev.halfmove;
      ctx.fx('crown', moveFrom(move));
    } else if (!isDrop(move) && moveFrom(move) === prev.royals[us] && (ctx.piece & TYPE_MASK) !== KING) {
      // The engine only follows real kings; an heir is followed here.
      next.royals[us] = moveTo(move);
    }
    if (prev.royals[ctx.them] >= 0 && next.royals[ctx.them] < 0) ctx.fx('regicide', ctx.capturedSq);
    syncExt(next);
  },

  nextTurn(ctx) {
    return isAction(ctx.move) ? ctx.us : ctx.them;
  },

  terminal(pos) {
    for (const c of [WHITE, BLACK] as Color[]) {
      if (pos.royals[c] < 0 && heirsOf(pos, c).length === 0) return win(other(c), 'no-heirs');
    }
    return null;
  },

  noMoves() {
    return draw('stalemate');
  },

  actionSan(pos, move) {
    const sq = moveFrom(move);
    return `👑${pieceDef(pos.board[sq] & TYPE_MASK).letter.toUpperCase()}${squareName(sq)}`;
  },

  evaluate(pos) {
    // A king is worth keeping, and each piece that could take over is a spare life.
    let score = 0;
    for (const c of [WHITE, BLACK] as Color[]) {
      const lives = heirsOf(pos, c).length + (pos.royals[c] >= 0 ? 1 : 0);
      let s = pos.royals[c] >= 0 ? 250 : 0;
      if (lives <= 1) s -= 700;
      else if (lives === 2) s -= 250;
      else if (lives === 3) s -= 80;
      score += c === WHITE ? s : -s;
    }
    return score;
  },

  reasonText: {
    'no-heirs': 'The last king fell with no heir left',
  },
};
